import { useState, useEffect, useCallback } from "react";
import { CheckCircle, ChatCircle, PaperPlaneTilt } from "@phosphor-icons/react";
import { API } from "./constants";

const DAY_LABELS = ["Lunedì", "Martedì", "Mercoledì", "Giovedì", "Venerdì", "Sabato", "Domenica"];

function ScaleField({ q, value, onChange }) {
  const min = q.min ?? 1;
  const max = q.max ?? 10;
  const opts = [];
  for (let i = min; i <= max; i++) opts.push(i);
  return (
    <div className="bg-[#0F0F0F] border border-[#1F1F1F] rounded-lg p-3">
      <p className="text-sm text-white font-medium mb-2">{q.label}</p>
      <div className="flex flex-wrap gap-1.5" role="radiogroup">
        {opts.map((n) => {
          const selected = Number(value) === n;
          return (
            <button
              key={n}
              type="button"
              onClick={() => onChange(n)}
              className={`min-w-[36px] h-9 px-3 rounded-md text-sm font-bold transition-all ${
                selected
                  ? "bg-[#007AFF] text-white"
                  : "bg-[#171717] border border-[#262626] text-zinc-400 hover:text-white hover:border-[#444]"
              }`}
              data-testid={`checkin-scale-${q.key}-${n}`}
            >
              {n}
            </button>
          );
        })}
      </div>
    </div>
  );
}

function NumberField({ q, value, onChange }) {
  return (
    <div className="bg-[#0F0F0F] border border-[#1F1F1F] rounded-lg p-3">
      <p className="text-sm text-white font-medium mb-2">{q.label}</p>
      <input
        type="number"
        inputMode="decimal"
        value={value ?? ""}
        onChange={(e) => onChange(e.target.value === "" ? "" : Number(e.target.value))}
        placeholder={q.placeholder || ""}
        min={q.min ?? undefined}
        max={q.max ?? undefined}
        className="w-full bg-[#171717] border border-[#262626] text-white rounded-md px-3 py-2 text-sm focus:outline-none focus:border-[#007AFF]"
        data-testid={`checkin-number-${q.key}`}
      />
    </div>
  );
}

function TextField({ q, value, onChange }) {
  return (
    <div className="bg-[#0F0F0F] border border-[#1F1F1F] rounded-lg p-3">
      <p className="text-sm text-white font-medium mb-2">{q.label}</p>
      <textarea
        value={value ?? ""}
        onChange={(e) => onChange(e.target.value)}
        placeholder={q.placeholder || ""}
        rows={3}
        className="w-full bg-[#171717] border border-[#262626] text-white rounded-md px-3 py-2 text-sm focus:outline-none focus:border-[#007AFF] resize-none"
        data-testid={`checkin-text-${q.key}`}
      />
    </div>
  );
}

export default function CheckinForm({ token }) {
  const [config, setConfig] = useState(null);
  const [last, setLast] = useState(null);
  const [answers, setAnswers] = useState({});
  const [loading, setLoading] = useState(true);
  const [submitting, setSubmitting] = useState(false);
  const [submitMsg, setSubmitMsg] = useState(null);

  const fetchAll = useCallback(async () => {
    try {
      const [c, l] = await Promise.all([
        fetch(`${API}/shared/${token}/checkin/config`).then((r) => r.json()),
        fetch(`${API}/shared/${token}/checkin/last-response`).then((r) => r.json()),
      ]);
      setConfig(c);
      setLast(l);
      if (l?.response?.answers) setAnswers(l.response.answers);
    } catch {
      setConfig({ enabled: false, questions: [] });
    } finally {
      setLoading(false);
    }
  }, [token]);

  useEffect(() => { fetchAll(); }, [fetchAll]);

  const updateAnswer = (key, value) => setAnswers((prev) => ({ ...prev, [key]: value }));

  const handleSubmit = async () => {
    if (submitting) return;
    const filled = Object.entries(answers).filter(([, v]) => v !== "" && v != null);
    if (filled.length === 0) {
      setSubmitMsg({ type: "error", text: "Compila almeno una domanda" });
      return;
    }
    setSubmitting(true);
    setSubmitMsg(null);
    try {
      const res = await fetch(`${API}/shared/${token}/checkin/responses`, {
        method: "POST",
        headers: { "Content-Type": "application/json" },
        body: JSON.stringify({ answers: Object.fromEntries(filled) }),
      });
      if (res.status === 403) {
        setSubmitMsg({ type: "error", text: "Il check-in è stato disattivato dal trainer." });
        await fetchAll();
        return;
      }
      if (!res.ok) throw new Error("submit failed");
      setSubmitMsg({ type: "success", text: "Check-in inviato. Grazie!" });
      await fetchAll();
    } catch {
      setSubmitMsg({ type: "error", text: "Errore nell'invio. Riprova." });
    } finally {
      setSubmitting(false);
    }
  };

  if (loading) {
    return (
      <div className="flex items-center justify-center py-12">
        <div className="w-6 h-6 border-2 border-[#007AFF] border-t-transparent rounded-full animate-spin" />
      </div>
    );
  }

  if (!config?.enabled) {
    return (
      <div className="text-center py-12 px-6">
        <ChatCircle size={36} weight="duotone" className="text-zinc-600 mx-auto mb-3" />
        <p className="text-sm font-medium text-zinc-400">Check-in non attivo</p>
        <p className="text-[12px] text-zinc-600 mt-1">
          Il tuo trainer non ha ancora attivato il check-in settimanale per te.
        </p>
      </div>
    );
  }

  const dayLabel = DAY_LABELS[Number(config.day_of_week) || 0];
  const alreadySubmitted = last?.submitted_this_week === true && submitMsg?.type !== "success";

  return (
    <div data-testid="shared-checkin-form" className="space-y-4">
      <div className="bg-[#171717] border border-[#262626] rounded-lg p-4">
        <div className="flex items-start gap-3">
          <ChatCircle size={20} weight="duotone" className="text-[#007AFF] flex-shrink-0 mt-0.5" />
          <div>
            <h2
              className="text-base font-bold tracking-tight uppercase text-white"
              style={{ fontFamily: "Barlow Condensed, sans-serif" }}
            >
              IL TUO CHECK-IN
            </h2>
            <p className="text-[12px] text-zinc-500 mt-0.5">
              Modulo settimanale · apre ogni {dayLabel}
            </p>
            {alreadySubmitted && (
              <div className="mt-2 inline-flex items-center gap-1.5 text-[11px] text-[#34C759] bg-[#34C759]/10 px-2 py-1 rounded-full">
                <CheckCircle size={12} weight="fill" />
                Già inviato per questa settimana — puoi aggiornare i valori
              </div>
            )}
          </div>
        </div>
      </div>

      <div className="space-y-3">
        {config.questions.map((q) => {
          const value = answers[q.key];
          if (q.type === "scale") return <ScaleField key={q.key} q={q} value={value} onChange={(v) => updateAnswer(q.key, v)} />;
          if (q.type === "number") return <NumberField key={q.key} q={q} value={value} onChange={(v) => updateAnswer(q.key, v)} />;
          return <TextField key={q.key} q={q} value={value} onChange={(v) => updateAnswer(q.key, v)} />;
        })}
      </div>

      {submitMsg && (
        <div
          className={`px-3 py-2 rounded-lg text-xs font-medium text-center ${
            submitMsg.type === "success"
              ? "bg-[#34C759]/10 text-[#34C759] border border-[#34C759]/30"
              : "bg-red-500/10 text-red-400 border border-red-500/30"
          }`}
          data-testid="checkin-submit-msg"
        >
          {submitMsg.text}
        </div>
      )}

      <button
        type="button"
        onClick={handleSubmit}
        disabled={submitting}
        className="w-full flex items-center justify-center gap-2 bg-[#007AFF] hover:bg-[#3395FF] text-white font-bold py-3 rounded-lg transition-colors active:scale-[0.98] disabled:opacity-50"
        data-testid="checkin-submit-button"
      >
        <PaperPlaneTilt size={16} weight="fill" />
        {submitting ? "Invio in corso..." : alreadySubmitted ? "Aggiorna risposta" : "Invia check-in"}
      </button>
    </div>
  );
}
